import { Pagination, PaginationResponse } from '@ekkogmbh/apisdk';
import {
  ArrowDownward,
  ArrowUpward,
  Clear,
  FilterListOutlined,
  Search,
  UnfoldMore,
  ViewColumnOutlined,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import useRequest from '../Hooks/useRequest';
import usePagination from '../Providers/PaginationProvider';
import useDataTable, { DataTableData } from '../Providers/DataTableProvider';
import { useSearchBar } from '../Providers/SearchBarProvider';

// =====================================================================
// Public column type — purpose-built so consumers don't need to know
// the underlying table library.
// =====================================================================

export interface AdminTableColumn<T> {
  /** Stable identifier; also used for sort/filter API keys. */
  field: string;
  headerName?: string;
  /** Initial pixel width. Default 150. */
  size?: number;
  /** Min size during resize. Default 60. */
  minSize?: number;
  /** Drag-to-resize. Default true. */
  resizable?: boolean;
  /** Column is sortable. Default true. */
  sortable?: boolean;
  /** Show in the Filters popover. Default true. */
  filterable?: boolean;
  /** Right-align numeric / action columns. Default left. */
  align?: 'left' | 'center' | 'right';
  /** Render the cell content for a row. */
  renderCell: (row: T) => ReactNode;
  /** Hide from the Columns popover (e.g. utility action columns). */
  hideInColumnsPopover?: boolean;
}

interface Props<T> {
  /** CAUTION: wrap in useCallback to avoid refetch on every parent render. */
  fetchItems: (pagination: Pagination) => Promise<PaginationResponse<T>>;
  columns: AdminTableColumn<T>[];
  /** Stable row identifier extractor. Defaults to `row.id`. */
  getRowId?: (row: T) => string | number;
  initialSort?: { field: string; sort: 'asc' | 'desc' };
  /** Map of column `field` → backend sort field (e.g. 'username' → 'U.username'). */
  sortFieldMap?: Record<string, string>;
  /** API filter key the header search writes into. Defaults to 'search'. */
  searchFilterField?: string;
  /** Outer height — the table container needs a bounded height. */
  height?: number | string;
  /** Left-side toolbar slot (typically the Add button). */
  primaryAction?: ReactNode;
}

// =====================================================================
// Per-cell highlighting (sourced from committed filter state).
// =====================================================================

interface SearchWordsContextValue {
  quick: string[];
  perField: Record<string, string>;
}
const SearchWordsCtx = createContext<SearchWordsContextValue>({ quick: [], perField: {} });

export function useSearchWords(field: string): string[] {
  const { quick, perField } = useContext(SearchWordsCtx);
  return useMemo(() => {
    const words: string[] = [...quick];
    const columnValue = perField[field];
    if (columnValue) {
      words.push(columnValue);
    }
    return words;
  }, [quick, perField, field]);
}

/**
 * Wraps cell text with <mark> highlights for the currently active search
 * terms — quick filter and per-column filter. Use inside `renderCell`.
 */
export const Highlight = ({ text, field }: { text: string; field: string }): React.JSX.Element => {
  const words = useSearchWords(field);
  if (words.length === 0 || !text) {
    return <>{text}</>;
  }
  return (
    <Box
      component="span"
      sx={{
        '& mark': {
          backgroundColor: 'secondary.main',
          color: 'secondary.contrastText',
          borderRadius: 0.5,
          px: 0.25,
          py: 0,
        },
      }}
    >
      <Highlighter autoEscape textToHighlight={text} searchWords={words} />
    </Box>
  );
};

// =====================================================================
// Column-filter state (shared via context with the Filters popover).
// =====================================================================

interface FilterableColumnMeta {
  field: string;
  headerName: string;
}

interface ColumnFilterContextValue {
  draft: Record<string, string>;
  setDraft: (field: string, value: string) => void;
  commit: () => void;
  clearField: (field: string) => void;
  clearAll: () => void;
  isSearchActive: boolean;
  filterableColumns: FilterableColumnMeta[];
}
const ColumnFilterCtx = createContext<ColumnFilterContextValue | null>(null);

// =====================================================================
// Shared popover recipe.
// =====================================================================

const SHARED_POPOVER_ANCHOR = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'right' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
};
const SHARED_PAPER_SX = { mt: 0.5 };

const PopoverHeader = ({ title, description }: { title: string; description?: string }): React.JSX.Element => (
  <Stack spacing={0.5}>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>
    {description && (
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    )}
  </Stack>
);

// =====================================================================
// Columns popover — drives TanStack column visibility.
// =====================================================================

interface ColumnsToolbarButtonProps {
  visibleColumnIds: string[];
  togglableColumns: { id: string; label: string }[];
  toggleColumn: (id: string) => void;
  setAllVisible: (visible: boolean) => void;
  resetVisibility: () => void;
}

const ColumnsToolbarButton = ({
  visibleColumnIds,
  togglableColumns,
  toggleColumn,
  setAllVisible,
  resetVisibility,
}: ColumnsToolbarButtonProps): React.JSX.Element => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const allOn = visibleColumnIds.length === togglableColumns.length;
  const isVisible = (id: string): boolean => visibleColumnIds.includes(id);
  const filtered = search
    ? togglableColumns.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
    : togglableColumns;

  return (
    <>
      <Tooltip title="Columns">
        <IconButton
          size="small"
          onClick={(e) => {
            setSearch('');
            setAnchor(e.currentTarget);
          }}
        >
          <ViewColumnOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        {...SHARED_POPOVER_ANCHOR}
        slotProps={{ paper: { sx: { ...SHARED_PAPER_SX, width: 300 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <PopoverHeader
              title="Show columns"
              description="Toggle visibility, search by name, or reset to defaults."
            />
            <Divider />

            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              variant="outlined"
              placeholder="Find column"
              fullWidth
              inputProps={{ autoComplete: 'off', 'data-1p-ignore': 'true', 'data-lpignore': 'true' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" onClick={() => setSearch('')}>
                      <Clear fontSize="inherit" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <Stack spacing={0.25} sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                  No columns match &ldquo;{search}&rdquo;
                </Typography>
              ) : (
                filtered.map((col) => (
                  <FormControlLabel
                    key={col.id}
                    control={<Switch size="small" checked={isVisible(col.id)} onChange={() => toggleColumn(col.id)} />}
                    label={col.label}
                    labelPlacement="start"
                    sx={{
                      ml: 0,
                      mr: 0,
                      justifyContent: 'space-between',
                      '& .MuiFormControlLabel-label': { fontSize: 14, flexGrow: 1 },
                    }}
                  />
                ))
              )}
            </Stack>

            <Divider />
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button size="small" color="inherit" onClick={() => setAllVisible(!allOn)}>
                {allOn ? 'Hide all' : 'Show all'}
              </Button>
              <Button size="small" color="inherit" onClick={resetVisibility}>
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

// =====================================================================
// Filters popover.
// =====================================================================

const FiltersToolbarButton = (): React.JSX.Element | null => {
  const ctx = useContext(ColumnFilterCtx);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!ctx || ctx.filterableColumns.length === 0) {
    return null;
  }

  const activeCount = Object.values(ctx.draft).filter((v) => v !== '').length;
  const disabled = ctx.isSearchActive;
  const tooltipTitle = disabled
    ? 'Clear the global search to filter by column'
    : activeCount > 0
      ? `Filters (${activeCount} active)`
      : 'Filters';

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Badge
            badgeContent={activeCount}
            color="primary"
            invisible={activeCount === 0}
            overlap="rectangular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-badge': {
                // Pill cluster wraps with `overflow: hidden` for rounded
                // corners, which clips the default half-outside transform.
                // Sit fully inside the IconButton at top:4 right:4.
                transform: 'translate(0, 0)',
                top: 4,
                right: 4,
                height: 14,
                minWidth: 14,
                fontSize: 9,
                fontWeight: 700,
                padding: '0 4px',
                borderRadius: 7,
              },
            }}
          >
            <IconButton size="small" disabled={disabled} onClick={(e) => setAnchor(e.currentTarget)}>
              <FilterListOutlined fontSize="small" />
            </IconButton>
          </Badge>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        {...SHARED_POPOVER_ANCHOR}
        slotProps={{ paper: { sx: { ...SHARED_PAPER_SX, width: 340 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <PopoverHeader
              title="Filter columns"
              description="Multiple filters combine with AND. Press Enter or Apply to commit."
            />
            <Divider />

            {ctx.filterableColumns.map((col) => {
              const value = ctx.draft[col.field] ?? '';
              return (
                <TextField
                  key={col.field}
                  value={value}
                  onChange={(e) => ctx.setDraft(col.field, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      ctx.commit();
                    }
                  }}
                  size="small"
                  variant="outlined"
                  label={col.headerName}
                  fullWidth
                  inputProps={{
                    autoComplete: 'off',
                    'data-1p-ignore': 'true',
                    'data-lpignore': 'true',
                    'data-form-type': 'other',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: value ? (
                      <InputAdornment position="end">
                        <IconButton size="small" edge="end" onClick={() => ctx.clearField(col.field)}>
                          <Clear fontSize="inherit" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              );
            })}

            <Divider />
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button size="small" color="inherit" disabled={activeCount === 0} onClick={() => ctx.clearAll()}>
                Clear all
              </Button>
              <Button size="small" variant="contained" onClick={() => ctx.commit()}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

// =====================================================================
// Toolbar (ControlBar) — left: primaryAction; right: pill cluster.
// =====================================================================

interface ControlBarProps {
  primaryAction?: ReactNode;
  columnsButton: ReactNode;
}

const ControlBar = ({ primaryAction, columnsButton }: ControlBarProps): React.JSX.Element => (
  <Stack direction="row" alignItems="center" sx={{ mb: 1.5, minHeight: 36 }}>
    {primaryAction}
    <Box sx={{ flexGrow: 1 }} />
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        '& .MuiIconButton-root': {
          color: 'text.secondary',
          borderRadius: 0,
          padding: '6px 10px',
          transition: 'color 120ms, background-color 120ms',
          '&:hover': {
            backgroundColor: 'action.hover',
            color: 'text.primary',
          },
          '&.Mui-disabled': { color: 'text.disabled' },
        },
        '& .MuiDivider-root': {
          borderColor: 'divider',
        },
      }}
    >
      {columnsButton}
      <Divider orientation="vertical" flexItem />
      <FiltersToolbarButton />
    </Box>
  </Stack>
);

// =====================================================================
// AdminTable
// =====================================================================

export function AdminTable<T extends object>({
  fetchItems,
  columns,
  getRowId,
  initialSort,
  sortFieldMap,
  searchFilterField = 'search',
  height = 'calc(100vh - 320px)',
  primaryAction,
}: Props<T>): React.JSX.Element {
  // ---- header-driven plumbing -----------------------------------------
  const { searchText, setSearchText } = useSearchBar();
  // Note: page/limit themselves are read by the page Header pagination
  // controls, not by us. We only need setPage (to reset to 1 on filter
  // commit) plus the refresh emitter to drive our fetch.
  const { page, setPage, refresh, subscribeRefresh, unsubscribeRefresh } = usePagination();
  const {
    setData,
    setTotalItemCount,
    registerSearchHandlers,
    registerClearColumnFilters,
    setHasColumnFilterInput,
    setIsSearchActive,
    committedFilter,
    commitFilter,
  } = useDataTable();

  // ---- local table state -----------------------------------------------
  const [rows, setRows] = useState<T[]>([]);
  const [isFetching, startFetching] = useRequest<PaginationResponse<T>>();

  // TanStack sorting state: [{ id, desc }]. We use single-column sort
  // (matches DataGrid behavior). Map initialSort onto it.
  const [sorting, setSorting] = useState<SortingState>(
    initialSort ? [{ id: initialSort.field, desc: initialSort.sort === 'desc' }] : [],
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilterDraft, setColumnFilterDraft] = useState<Record<string, string>>({});

  const draftFilter = useMemo((): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [field, value] of Object.entries(columnFilterDraft)) {
      if (value) {
        out[field] = value;
      }
    }
    if (searchText) {
      out[searchFilterField] = searchText;
    }
    return out;
  }, [columnFilterDraft, searchText, searchFilterField]);

  const draftFilterRef = useRef(draftFilter);
  draftFilterRef.current = draftFilter;
  const committedFilterRef = useRef(committedFilter);
  committedFilterRef.current = committedFilter;
  const pageRef = useRef(page);
  pageRef.current = page;
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;

  // ---- TanStack column model ------------------------------------------
  const tanstackColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col) => ({
        id: col.field,
        // TanStack's `getCanSort()` requires `accessorFn` to be defined,
        // regardless of `enableSorting` — without one, every column is
        // treated as non-sortable. We render via `cell` so the return
        // value doesn't matter, but the function has to exist.
        accessorFn: (row: T) => (row as unknown as Record<string, unknown>)[col.field],
        header: col.headerName ?? col.field,
        size: col.size ?? 150,
        minSize: col.minSize ?? 60,
        enableResizing: col.resizable !== false,
        enableSorting: col.sortable !== false,
        enableHiding: !col.hideInColumnsPopover,
        meta: { align: col.align ?? 'left' },
        cell: (info) => col.renderCell(info.row.original),
      })),
    [columns],
  );

  const filterableColumns = useMemo<FilterableColumnMeta[]>(
    () =>
      columns
        .filter((c) => c.filterable !== false && !c.hideInColumnsPopover)
        .map((c) => ({ field: c.field, headerName: c.headerName ?? c.field })),
    [columns],
  );

  // ---- TanStack table instance ----------------------------------------
  const table = useReactTable<T>({
    data: rows,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    columnResizeMode: 'onChange',
    getRowId: getRowId ? (row) => String(getRowId(row)) : undefined,
  });

  // ---- mount: flush stale provider state ------------------------------
  const [staleCleared, setStaleCleared] = useState(false);
  useEffect(() => {
    setSearchText('');
    setColumnFilterDraft({});
    commitFilter({});
    setData([]);
    setTotalItemCount(0);
    setStaleCleared(true);
    return () => {
      setData([]);
      setTotalItemCount(0);
      setHasColumnFilterInput(false);
      setIsSearchActive(false);
    };
    // Run exactly once per mount.
  }, []);

  // ---- fetch (driven by refresh emit) ---------------------------------
  const requestIdRef = useRef(0);

  const fetchData = useCallback(
    (pagination: Pagination) => {
      const requestId = ++requestIdRef.current;
      const sort = sortingRef.current[0];
      const apiSort = sort?.id ? (sortFieldMap?.[sort.id] ?? sort.id) : undefined;
      const committed = committedFilterRef.current;

      const apiPagination: Pagination = {
        page: pagination.page,
        limit: pagination.limit,
        sort: apiSort,
        direction: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
        filter: Object.keys(committed).length > 0 ? committed : undefined,
      };

      startFetching(
        () => fetchItems(apiPagination),
        (result) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          const response = result as PaginationResponse<T>;
          const items = (response.items ?? []) as T[];
          const itemCount = response.totalItemCount ?? 0;
          setRows(items);
          setData(items as unknown as DataTableData[]);
          setTotalItemCount(itemCount);
        },
      );
    },
    [fetchItems, sortFieldMap, startFetching, setData, setTotalItemCount],
  );

  useEffect(() => {
    if (!staleCleared) {
      return;
    }
    subscribeRefresh(fetchData);
    return () => unsubscribeRefresh(fetchData);
  }, [fetchData, staleCleared, subscribeRefresh, unsubscribeRefresh]);

  // Drive every refetch off `committedFilter` changing. This effect fires
  // AFTER React has committed the new committedFilter to state, so
  // `committedFilterRef.current` (read by fetchData) is guaranteed fresh.
  //
  // A synchronous `refresh()` directly after `commitFilter(...)` would
  // run with a stale ref and require the user to press Enter twice — the
  // first press updated the state but fetchData read the previous value.
  //
  // This effect also covers the very first fetch on mount: the mount-
  // cleanup effect calls `commitFilter({})`, which becomes the initial
  // committedFilter change once `staleCleared` flips to true.
  useEffect(() => {
    if (!staleCleared) {
      return;
    }
    if (pageRef.current !== 1) {
      // setPage triggers refresh via PaginationProvider's page-change
      // effect — single round-trip, no risk of double-fetch.
      setPage(1);
    } else {
      refresh();
    }
  }, [committedFilter, staleCleared, setPage, refresh]);

  // ---- commit + mutual-exclusivity wiring -----------------------------
  // Commit handlers just snapshot the draft into committedFilter; the
  // useEffect above does the refresh once React has committed.
  const commitAndRefresh = useCallback(() => {
    commitFilter(draftFilterRef.current);
  }, [commitFilter]);

  const clearHeaderSearch = useCallback(() => {
    setSearchText('');
    const next = { ...draftFilterRef.current };
    delete next[searchFilterField];
    commitFilter(next);
  }, [setSearchText, commitFilter, searchFilterField]);

  useEffect(() => {
    registerSearchHandlers(commitAndRefresh, clearHeaderSearch);
  }, [commitAndRefresh, clearHeaderSearch, registerSearchHandlers]);

  useEffect(() => {
    const hasInput = Object.values(columnFilterDraft).some((v) => v !== '');
    setHasColumnFilterInput(hasInput);
  }, [columnFilterDraft, setHasColumnFilterInput]);

  useEffect(() => {
    setIsSearchActive(searchText !== '');
  }, [searchText, setIsSearchActive]);

  const clearAllColumnFilters = useCallback(() => {
    setColumnFilterDraft({});
    const next = { ...draftFilterRef.current };
    for (const key of Object.keys(next)) {
      if (key !== searchFilterField) {
        delete next[key];
      }
    }
    commitFilter(next);
  }, [commitFilter, searchFilterField]);

  useEffect(() => {
    registerClearColumnFilters(clearAllColumnFilters);
  }, [clearAllColumnFilters, registerClearColumnFilters]);

  const columnFilterCtx = useMemo<ColumnFilterContextValue>(
    () => ({
      draft: columnFilterDraft,
      setDraft: (field, value) => setColumnFilterDraft((prev) => ({ ...prev, [field]: value })),
      commit: () => commitAndRefresh(),
      clearField: (field) => {
        setColumnFilterDraft((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
        const explicit = { ...draftFilterRef.current };
        delete explicit[field];
        commitFilter(explicit);
      },
      clearAll: clearAllColumnFilters,
      isSearchActive: searchText !== '',
      filterableColumns,
    }),
    [columnFilterDraft, commitAndRefresh, commitFilter, searchText, filterableColumns, clearAllColumnFilters],
  );

  // ---- sort changes trigger refetch (no page reset) -------------------
  const isInitialSort = useRef(true);
  useEffect(() => {
    if (isInitialSort.current) {
      isInitialSort.current = false;
      return;
    }
    refresh();
  }, [sorting, refresh]);

  // ---- highlight context ----------------------------------------------
  const searchWords = useMemo<SearchWordsContextValue>(() => {
    const perField: Record<string, string> = {};
    let quick: string[] = [];
    for (const [key, value] of Object.entries(committedFilter)) {
      if (!value) {
        continue;
      }
      if (key === searchFilterField) {
        quick = [value];
      } else {
        perField[key] = value;
      }
    }
    return { quick, perField };
  }, [committedFilter, searchFilterField]);

  // ---- Columns popover content ----------------------------------------
  const togglableColumns = useMemo(
    () =>
      columns
        .filter((c) => !c.hideInColumnsPopover && c.headerName && c.headerName.trim() !== '')
        .map((c) => ({ id: c.field, label: c.headerName as string })),
    [columns],
  );

  const visibleColumnIds = useMemo(
    () => togglableColumns.filter((c) => columnVisibility[c.id] !== false).map((c) => c.id),
    [togglableColumns, columnVisibility],
  );

  const toggleColumn = useCallback(
    (id: string) => setColumnVisibility((prev) => ({ ...prev, [id]: prev[id] === false })),
    [],
  );
  const setAllVisible = useCallback(
    (visible: boolean) => {
      setColumnVisibility(() => {
        const next: VisibilityState = {};
        for (const c of togglableColumns) {
          next[c.id] = visible;
        }
        return next;
      });
    },
    [togglableColumns],
  );
  const resetVisibility = useCallback(() => setColumnVisibility({}), []);

  // ---- render ----------------------------------------------------------
  return (
    <SearchWordsCtx.Provider value={searchWords}>
      <ColumnFilterCtx.Provider value={columnFilterCtx}>
        <Box sx={{ width: '100%', height, display: 'flex', flexDirection: 'column' }}>
          <ControlBar
            primaryAction={primaryAction}
            columnsButton={
              <ColumnsToolbarButton
                visibleColumnIds={visibleColumnIds}
                togglableColumns={togglableColumns}
                toggleColumn={toggleColumn}
                setAllVisible={setAllVisible}
                resetVisibility={resetVisibility}
              />
            }
          />

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              borderRadius: 1.5,
              position: 'relative',
            }}
          >
            <TableContainer sx={{ height: '100%' }}>
              <Table
                stickyHeader
                sx={{
                  tableLayout: 'fixed',
                  // Fill the container when there's extra width, scroll
                  // when there isn't. With `table-layout: fixed`, columns
                  // stretch proportionally to their explicit pixel widths
                  // to fill the table — so the visual rhythm of "this
                  // column is wider than that one" is preserved.
                  width: '100%',
                  minWidth: table.getTotalSize(),
                }}
              >
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} sx={{ height: 48 }}>
                      {headerGroup.headers.map((header) => {
                        const colDef = header.column.columnDef;
                        const align =
                          (colDef.meta as { align?: 'left' | 'center' | 'right' } | undefined)?.align ?? 'left';
                        const canSort = header.column.getCanSort();
                        const sortDir = header.column.getIsSorted();
                        const canResize = header.column.getCanResize();
                        return (
                          <TableCell
                            key={header.id}
                            align={align}
                            sx={{
                              position: 'relative',
                              width: header.getSize(),
                              backgroundColor: 'action.hover',
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              textTransform: 'uppercase',
                              fontSize: 12,
                              color: 'text.secondary',
                              userSelect: 'none',
                              p: 1.5,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              justifyContent={
                                align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'
                              }
                              sx={{
                                cursor: canSort ? 'pointer' : 'default',
                              }}
                              onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                            >
                              <Box
                                component="span"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </Box>
                              {canSort && (
                                <Box component="span" sx={{ display: 'inline-flex', opacity: sortDir ? 1 : 0.35 }}>
                                  {sortDir === 'asc' ? (
                                    <ArrowUpward sx={{ fontSize: 14 }} />
                                  ) : sortDir === 'desc' ? (
                                    <ArrowDownward sx={{ fontSize: 14 }} />
                                  ) : (
                                    <UnfoldMore sx={{ fontSize: 14 }} />
                                  )}
                                </Box>
                              )}
                            </Stack>
                            {canResize && (
                              <Box
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  // Wide hit area for easy grabbing…
                                  width: 6,
                                  cursor: 'col-resize',
                                  userSelect: 'none',
                                  touchAction: 'none',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  alignItems: 'center',
                                  // …with a slim, rounded, half-height
                                  // divider line at rest. Mirrors how
                                  // MUI's vertical Divider with flexItem
                                  // typically sits centered with margins
                                  // — feels more deliberate than a hard
                                  // edge-to-edge line.
                                  '&::after': {
                                    content: '""',
                                    width: '1px',
                                    height: '55%',
                                    borderRadius: '1px',
                                    backgroundColor: 'divider',
                                    transition: 'background-color 140ms ease, width 140ms ease, height 140ms ease',
                                  },
                                  // Subtle "grow" feedback on hover: line
                                  // elongates toward full height and picks
                                  // up the primary color.
                                  '&:hover::after, &:active::after': {
                                    backgroundColor: 'primary.main',
                                    width: '2px',
                                    height: '85%',
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {table.getRowModel().rows.length === 0 && !isFetching && (
                    <TableRow>
                      <TableCell colSpan={table.getVisibleLeafColumns().length} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No results
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        height: 56,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const align =
                          (cell.column.columnDef.meta as { align?: 'left' | 'center' | 'right' } | undefined)?.align ??
                          'left';
                        return (
                          <TableCell
                            key={cell.id}
                            align={align}
                            sx={{
                              width: cell.column.getSize(),
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {isFetching && (
              <Box
                sx={{
                  position: 'absolute',
                  // Center inside the Paper (which has `position: relative`)
                  // — the column-header strip is sticky at the top so it
                  // takes ~48px, leaving the rest of the Paper for rows.
                  // Centering on the Paper puts the spinner well below the
                  // header instead of overlapping it.
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Paper>
        </Box>
      </ColumnFilterCtx.Provider>
    </SearchWordsCtx.Provider>
  );
}
